import generateHash from '../utils/generateHash';
import { usePageMeta } from './usePageMeta';
import { useTranslation } from 'react-i18next';

type MessageMapper = (shareUrl: string) => string;
type EmailMapper = (
  shareUrl: string,
) => {
  subject: string;
  body: string;
};

interface MessageOverrides {
  whatsApp?: MessageMapper;
  twitter?: MessageMapper;
  sms?: MessageMapper;
  email?: EmailMapper;
}

export function useShareUrls(
  messageMappers: MessageOverrides,
  shareUrl?: string,
) {
  const facebook = useFacebook(shareUrl);
  const facebookMessenger = useFacebookMessenger(shareUrl);
  const twitter = useTwitter(shareUrl, messageMappers.twitter);
  const email = useEmail(shareUrl, messageMappers.email);
  const whatsApp = useWhatsapp(shareUrl, messageMappers.whatsApp);
  const sms = useSms(shareUrl, messageMappers.sms);
  const linkedin = useLinkedIn(shareUrl);
  const copyLink = useCopyLink(shareUrl);

  return {
    facebook,
    facebookMessenger,
    twitter,
    email,
    whatsApp,
    sms,
    linkedin,
    copyLink,
  };
}

function useShareUrl(shareUrl?: string, queryString?: string) {
  const { currentUrl } = usePageMeta();

  if (shareUrl) return shareUrl;

  if (!queryString) return currentUrl;

  return currentUrl.includes('?')
    ? `${currentUrl}&${queryString}`
    : `${currentUrl}?${queryString}`;
}

function useFacebook(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=facebook&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-share&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useFacebookMessenger(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=messenger&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-share&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useCopyLink(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=copyLink&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-share&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useTwitter(shareUrl?: string, messageMapper?: MessageMapper) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=Twitter&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-tweet&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);
  const message = messageMapper
    ? messageMapper(url)
    : t('common.shareMessageFallback');

  return {
    url,
    message,
  };
}

function useEmail(
  shareUrl?: string,
  messageMapper?: (url: string) => { subject: string; body: string },
) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=Sharethis&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-email&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  const { subject, body } = messageMapper
    ? messageMapper(url)
    : { subject: '', body: '' };

  return {
    subject,
    body,
  };
}

function useWhatsapp(shareUrl?: string, messageMapper?: MessageMapper) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=whatsapp&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-whatsapp&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  const message = messageMapper
    ? messageMapper(url)
    : t('common.shareMessageFallback');

  return {
    url,
    message,
  };
}

function useSms(shareUrl?: string, messageMapper?: MessageMapper) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=sms&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-sms&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  const message = messageMapper
    ? messageMapper(url)
    : t('common.shareMessageFallback');

  return {
    url,
    message,
  };
}

function useLinkedIn(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=LinkedIn&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-linkedin&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return url;
}
